import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles } from "lucide-react";
import TopBar from "../../components/layout/TopBar";
import BottomNav from "../../components/layout/BottomNav";
import { useAuthStore } from "../../store/authStore";
import { payForSubscription } from "../../services/razorpay";
import { toast } from "sonner";

const PLANS = [
  {
    id: "free",
    name: "Free",
    monthly: 0,
    annual: 0,
    features: ["Basic AI workouts (3/week)", "Food log (manual)", "Trainer browse", "Health dashboard"],
  },
  {
    id: "pro",
    name: "Pro",
    monthly: 499,
    annual: 3999,
    badge: "Most popular",
    accent: true,
    features: [
      "Unlimited AI workouts",
      "AI Food Scanner (unlimited)",
      "AI Nutritionist chat",
      "Custom meal plans",
      "Priority trainer booking",
    ],
  },
  {
    id: "elite",
    name: "Elite",
    monthly: 999,
    annual: 7999,
    features: [
      "Everything in Pro",
      "Live AI Trainer with form check",
      "Video calls with trainers",
      "1-on-1 nutritionist support",
      "Priority customer support",
    ],
  },
];

export default function Pricing() {
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState(null);
  const navigate = useNavigate();
  const { user, checkAuth } = useAuthStore();
  const currentPlan = user?.subscription_tier || "free";

  const choose = async (plan) => {
    if (plan.id === "free") {
      toast.success("You're already on the Free plan");
      return;
    }
    if (currentPlan === plan.id) {
      toast.success(`${plan.name} is your current plan`);
      return;
    }
    setBusy(plan.id);
    try {
      const res = await payForSubscription({
        plan: plan.id,
        billing_cycle: cycle,
      });
      toast.success(`Welcome to ${plan.name}`, {
        description: `+200 FitPoints. Plan active until ${cycle === "annual" ? "next year" : "next month"}.`,
      });
      await checkAuth();
      setTimeout(() => navigate("/home"), 1200);
    } catch (e) {
      if (e.message !== "dismissed") {
        toast.error("Payment failed", { description: e.message });
      }
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="app-page" data-testid="pricing-page">
      <TopBar title="Pricing" back showProfile={false} />
      <main className="max-w-2xl mx-auto px-4 pt-3 pb-8">
        <div className="text-center">
          <h1 className="font-display text-[32px] leading-tight">Train smarter</h1>
          <p className="text-[14px] text-ink-soft mt-1.5">Pick a plan. Cancel anytime.</p>
        </div>

        <div className="mt-5 inline-flex p-1 rounded-pill bg-white border border-line mx-auto" style={{ display: "flex" }} data-testid="billing-toggle">
          {[
            { id: "monthly", label: "Monthly" },
            { id: "annual", label: "Annual · Save 30%" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setCycle(t.id)}
              data-testid={`cycle-${t.id}`}
              className="px-5 h-10 rounded-pill text-[13px] font-medium transition-all"
              style={{
                background: cycle === t.id ? "var(--accent-blue)" : "transparent",
                color: cycle === t.id ? "#000" : "var(--text-secondary)",
                borderRadius: 100,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
          {PLANS.map((p) => (
            <div
              key={p.id}
              className="relative bg-white rounded-[16px] p-5 border-2 transition-all"
              style={{
                borderColor: p.accent ? "var(--accent-blue)" : "var(--border)",
                boxShadow: p.accent ? "var(--shadow-card)" : "var(--shadow-soft)",
                transform: p.accent ? "translateY(-4px)" : "none",
              }}
              data-testid={`plan-${p.id}`}
            >
              {p.badge && (
                <span
                  className="absolute -top-2.5 right-4 px-2.5 py-0.5 rounded-pill text-[10.5px] font-medium uppercase tracking-wider"
                  style={{ background: "var(--accent-blue)", color: "#000", borderRadius: 100 }}
                >
                  {p.badge}
                </span>
              )}
              <div className="font-display text-[24px]">{p.name}</div>
              <div className="mt-2">
                <span className="font-display text-[36px]">
                  {p[cycle] === 0 ? "Free" : `₹${p[cycle].toLocaleString("en-IN")}`}
                </span>
                {p[cycle] !== 0 && (
                  <span className="text-[13px] text-ink-soft ml-1">/{cycle === "annual" ? "yr" : "mo"}</span>
                )}
              </div>
              <ul className="mt-5 space-y-2">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-[13.5px] text-ink-soft">
                    <Check size={15} style={{ color: "var(--accent-blue)" }} className="mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => choose(p)}
                data-testid={`choose-${p.id}`}
                disabled={busy === p.id}
                className="mt-6 w-full h-11 rounded-pill font-medium transition-all disabled:opacity-60"
                style={{
                  background: p.id === "free" ? "white" : p.accent ? "var(--accent-blue)" : "var(--text-primary)",
                  color: p.id === "free" ? "var(--text-secondary)" : p.accent ? "#000" : "white",
                  border: p.id === "free" ? "1px solid var(--border)" : "none",
                  borderRadius: 100,
                }}
              >
                {busy === p.id
                  ? "Opening checkout…"
                  : currentPlan === p.id
                  ? "Current plan"
                  : p.id === "free"
                  ? "Free forever"
                  : `Upgrade to ${p.name}`}
              </button>
            </div>
          ))}
        </div>

        <div className="mt-6 text-center text-[12px] text-ink-muted flex items-center justify-center gap-1.5">
          <Sparkles size={12} /> All plans include the warm-up Indian-aware AI insights
        </div>
      </main>
      <BottomNav />
    </div>
  );
}
